import React, { FC, useEffect, useState } from 'react';
import { useParams } from 'next/navigation';
import { Props } from 'react-modal';
import Button from 'components/commercetools-ui/atoms/button';
import Dropdown from 'components/commercetools-ui/atoms/dropdown';
import Input from 'components/commercetools-ui/atoms/input';
import Typography from 'components/commercetools-ui/atoms/typography';
import useTranslation from 'providers/i18n/hooks/useTranslation';
import { useAccount } from 'frontastic';
import {
  companyTypeOptions,
  stateOptionsAU,
  stateOptionsNZ,
  rolesOptionsAccount,
  PHONE_PATTERN,
} from '../../authentication/company-registration/data';
import Markdown from '../../markdown';
import Spinner from '../../spinner';
import { useRouter } from 'next/navigation';

const MyAccount: FC<Props> = () => {
  const { locale } = useParams();
  const { translate } = useTranslation();
  const { account, update } = useAccount();
  const [formErrors, setFormErrors] = useState<any>({});
  const [error, setError] = useState<string>('');
  const [formData, setFormData] = useState<any>({});
  const [loading, setLoading] = useState<any>(true);
  const [success, setSuccess] = useState<boolean>(false);

  useEffect(() => {
    if (account?.accountId) {
      setFormData({
        ...account,
        country: account?.country ? account?.country : locale === 'au' ? 'Austrailia' : 'New Zealand',
      });
      setLoading(false);
    }
  }, [account]);

  const requiredFields: any = [
    { name: 'email', message: translate(`error.auth.email.required`) },
    { name: 'firstName', message: translate('error.first.name.required') },
    { name: 'lastName', message: translate('error.last.name.required') },
    { name: 'role', message: 'Role is required.' },
    { name: 'phone', message: translate(`phone is required and must be upto 12 digits`) },
    { name: 'companyName', message: translate(`error.company.name.required`) },
    { name: 'company_type', message: translate(`error.company.type.required`) },
    { name: 'country', message: translate(`error.country.required`) },
    { name: 'state', message: translate(`error.state.required`) },
  ];

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setLoading(true);

    const errors: any = {};

    requiredFields.forEach((field: { name: string | number; message: any }) => {
      if (!formData[field.name] || formData[field.name].includes('*')) {
        errors[field.name] = field.message;
      }
    });

    if (!PHONE_PATTERN.test(formData.phone)) {
      errors['phone'] = translate('phone number must be of 12 digits');
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setLoading(false);
      return;
    }

    try {
      const payload = {
        firstName: formData?.firstName || '',
        lastName: formData?.lastName || '',
        role: formData?.role || '',
        phone: formData?.phone || '',
        companyName: formData?.companyName || '',
        companyType: formData?.company_type || '',
        country: formData?.country || '',
        state: formData?.state || '',
        legal_entity: account?.legal_entity || locale,
        postal_code: formData?.postal_code || '',
        customer_marketing_consent: formData?.customer_marketing_consent ? 'yes' : '',
      };
      const responseBody: any = await update(payload);

      if (!!responseBody?.accountId) {
        setSuccess(true);
        setError('');
        setFormErrors({});

        setTimeout(() => {
          setSuccess(false);
        }, 2000);
      }
    } catch (error: any) {
      const errorMessage = (error?.message || error?.toString() || 'Something went wrong!').replace('Error: ', '');

      if (errorMessage == 'Failed to fetch') {
        setError(translate(`error.no.internet`));
        return;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleBlur = (name: string) => {
    const errors: any = {};
    if (!formData[name]) {
      errors[name] = requiredFields.find((item: { name: string }) => item.name === name).message;
      setFormErrors({ ...formErrors, ...errors });
    } else {
      errors[name] = '';
      setFormErrors({ ...formErrors, ...errors });
    }
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({
      ...prev,
      [name]: value,
    }));
    handleBlur(name);
  };

  const handleDropdownChange = (name: string | number, option: any) => {
    const errors: any = {};
    if (option.value.includes('*')) {
      errors[name] = requiredFields.find((item: { name: string }) => item.name === name).message;
      setFormErrors({ ...formErrors, ...errors });
    } else {
      errors[name] = '';
      setFormErrors({ ...formErrors, ...errors });
    }
    setFormData((prev: any) => ({
      ...prev,
      [name]: option.value,
    }));
  };

  const handleCheckboxChange = (e: any) => {
    const { name, checked } = e.target;
    setFormData((prev: any) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const disableDropdown = account?.legal_entity !== locale;

  return loading ? (
    <Spinner />
  ) : (
    <div className="w-full">
      <Typography className="font-medium text-32 text-black">My Account</Typography>
      <form onSubmit={handleSubmit}>
        <Typography className="py-[30px] text-22 font-medium text-black">About you</Typography>
        <div className="padding-r-38-lg grid sm:grid-cols-1 md:grid-cols-1  lg:grid-cols-3 lg:gap-10">
          <Input
            name="firstName"
            placeholder={'First Name*'}
            value={formData['firstName']}
            maxLength={50}
            required
            error={formErrors['firstName']}
            onChange={handleChange}
            tabIndex={0}
            onBlur={() => handleBlur('firstName')}
            className={`mb-20 text-xs  ${
              formErrors['firstName'] ? '!border-red-500' : '!border-[#A7A7A7]'
            } !rounded-[6px] `}
            errorClassName="!absolute top-30 left-4"
          />

          <Input
            name="email"
            placeholder={translate('common.your.email')}
            value={formData['email']}
            maxLength={100}
            required
            disabled
            error={formErrors['email']}
            onChange={handleChange}
            tabIndex={0}
            className={`mb-20 !p-13 text-xs ${
              formErrors['email'] ? '!border-red-500' : '!border-[#A7A7A7]'
            } !rounded-[6px] !bg-gray-100`}
            errorClassName="!absolute top-30 left-4"
          />

          <div className="relative">
            <Dropdown
              error={formErrors['role']}
              selectOptions={rolesOptionsAccount}
              selectOnChange={(val) => handleDropdownChange('role', val)}
              defaultValue={formData?.role ? { name: formData?.role, value: formData?.role } : rolesOptionsAccount[0]}
              optionsClassName="!top-37 xl:!top-[38px] md:!top-38 border-light-gray "
              clearDropdown={false}
              disableFirstOption
              selectDropdownClassNames={`${
                !formData?.role ? 'text-secondary-black' : '!text-black'
              } !rounded-[6px] text-14 mb-20 border-light-gray text-left overflow-hidden !h-fit !px-13 !py-[8.5px] w-full `}
            />
            {formErrors['role'] && (
              <Typography
                className="text-error-600 !absolute left-4 top-30 my-12 text-12 font-medium leading-[16px]"
                as="span"
              >
                {formErrors['role']}
              </Typography>
            )}
          </div>
        </div>
        <div className="padding-r-38-lg grid sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-3 lg:gap-10">
          <Input
            name={'lastName'}
            placeholder={'Last Name*'}
            value={(formData['lastName'] as string) ?? ''}
            maxLength={50}
            required
            onBlur={() => handleBlur('lastName')}
            error={formErrors['lastName']}
            onChange={handleChange}
            tabIndex={0}
            className={`mb-20 !p-13 text-xs ${
              formErrors['lastName'] ? '!border-red-500' : '!border-[#A7A7A7]'
            } !rounded-[6px] `}
            errorClassName="!absolute top-30 left-4"
          />

          <Input
            type="text"
            name="phone"
            placeholder={translate('common.phone.required')}
            value={(formData['phone'] as string) ?? ''}
            error={formErrors['phone']}
            onChange={handleChange}
            tabIndex={0}
            onBlur={() => handleBlur('phone')}
            maxLength={12}
            autoComplete=""
            className={`!p-13 text-xs ${formErrors['phone'] ? '!border-red-500' : '!border-[#A7A7A7]'} !rounded-[6px] `}
            errorClassName="!absolute top-30 left-4"
          />
        </div>
        <Typography className="py-[30px] text-22 font-medium text-black">About your company</Typography>

        <div className="padding-r-38-lg grid sm:grid-cols-1  md:grid-cols-1 lg:grid-cols-3 lg:gap-10">
          <Input
            name="companyName"
            placeholder={translate('common.company.required')}
            value={formData['companyName']}
            required
            error={formErrors['companyName']}
            onChange={handleChange}
            onBlur={() => handleBlur('companyName')}
            tabIndex={0}
            maxLength={150}
            disabled={disableDropdown}
            className={`mb-20 !p-13 text-xs ${disableDropdown && '!bg-gray-100'} ${
              formErrors['companyName'] ? '!border-red-500' : '!border-[#A7A7A7]'
            } !rounded-[6px] `}
            errorClassName="!absolute top-30 left-4"
          />

          <Input
            name="country"
            placeholder={translate('country')}
            value={formData?.country}
            maxLength={100}
            required
            disabled
            error={formErrors['country']}
            onChange={handleChange}
            onBlur={() => handleBlur('country')}
            tabIndex={0}
            className={`mb-20 !rounded-[6px] !p-13 text-xs !bg-gray-100`}
            errorClassName="!absolute top-30 left-4"
          />
        </div>

        <div className="padding-r-38-lg grid sm:grid-cols-1  md:grid-cols-1 lg:grid-cols-3 lg:gap-10">
          <div className="relative">
            <Dropdown
              error={formErrors['company_type']}
              selectOptions={companyTypeOptions}
              defaultValue={
                formData?.company_type
                  ? { name: formData?.company_type, value: formData?.company_type }
                  : companyTypeOptions[0]
              }
              disableFirstOption
              disabled={disableDropdown}
              selectOnChange={(val) => handleDropdownChange('company_type', val)}
              optionsClassName="!top-37 xl:!top-[38px] md:!top-38 border-light-gray "
              clearDropdown={false}
              selectDropdownClassNames={`  ${disableDropdown && '!bg-gray-100'} ${
                !formData['company_type'] ? 'text-secondary-black' : '!text-black'
              } !rounded-[6px] mb-20 text-14 border-light-gray text-left overflow-hidden !h-fit !px-13 !py-[8.5px] w-full `}
            />
            {formErrors['company_type'] && (
              <Typography
                className="text-error-600 !absolute left-4 top-30 my-12 text-12 font-medium leading-[16px]"
                as="span"
              >
                {formErrors['company_type']}
              </Typography>
            )}
          </div>

          <div className="relative">
            <Dropdown
              disabled={disableDropdown}
              error={formErrors['state']}
              selectOptions={locale === 'au' ? stateOptionsAU : stateOptionsNZ}
              defaultValue={
                formData?.state
                  ? { name: formData?.state, value: formData?.state }
                  : locale === 'au'
                  ? stateOptionsAU[0]
                  : stateOptionsNZ[0]
              }
              disableFirstOption
              selectOnChange={(val) => handleDropdownChange('state', val)}
              optionsClassName="!top-37 xl:!top-[38px]  md:!top-38  border-light-gray"
              clearDropdown={false}
              selectDropdownClassNames={` ${disableDropdown && '!bg-gray-100'} ${
                !formData['state'] ? 'text-secondary-black' : '!text-black'
              } !rounded-[6px] mb-20 text-14 border-light-gray text-left overflow-hidden !h-fit !px-13 !py-[8.5px] w-full `}
            />
            {formErrors['state'] && (
              <Typography
                className="text-error-600 !absolute left-4 top-30 my-12 text-12 font-medium leading-[16px]"
                as="span"
              >
                {formErrors['state']}
              </Typography>
            )}
          </div>
        </div>

        <div className="md:padding-t-40 padding-r-38-lg pt-20">
          <div className={`flex items-center gap-8 ${formErrors?.customer_marketing_consent ? '' : ' mb-20'}`}>
            <input
              type="checkbox"
              name="customer_marketing_consent"
              checked={formData?.customer_marketing_consent}
              onChange={handleCheckboxChange}
              className="!border-1 !h-20 !w-20 rounded-sm p-0 !text-black text-primary-black focus:border-gray-500 focus:ring-0"
            />
            <Markdown
              markdown={
                'I would like to receive marketing communications, including updates regarding new ' +
                'products and services by Caroma, whether by hard copy and electronic mail and by phone; and I understand I can unsubscribe at any time'
              }
              className=" font-normal text-16 text-black"
            />
          </div>
          {formErrors?.customer_marketing_consent && (
            <span className="padding-l-4 font-medium text-error-600 text-12">
              {formErrors?.customer_marketing_consent}
            </span>
          )}
        </div>

        <div className="my-32 md:!mb-[80px] md:!mt-[40px]">
          {error && <p className="font-medium text-error-600 pb-24 text-14">{error}</p>}
          {error && <p className="font-medium text-error-600 pb-24 text-14">{error}</p>}

          {success && (
            <Typography className="!mb-20 text-14 text-green-600">Profile has been successfully updated.</Typography>
          )}
          <div className="flex flex-row items-end justify-between">
            <div className="flex flex-row justify-between">
              <Button
                size="s"
                type="submit"
                className="mb-16 mr-[30px] rounded-none text-16 leading-tight md:mb-20"
                disabled={loading}
              >
                Update
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default MyAccount;
